import React, { createContext, useEffect, useState } from 'react';

import { setContextCompany } from 'apis/UserAPI';
import { WIDGET_FILTER_OPTIONS, WIDGET_FILTER_TYPES } from 'common/Widgets/constants';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { handleUpdateAccessToken } from 'store/slices/authSlice';
import { handleSetContextCompany } from 'store/slices/globalSlice';
import { CompanyDataType } from 'types/actors';
import { SelectAdvancedFilterOptionType } from 'types/common';
import { CommunityType } from 'types/communities';
import { RecommendationFilterType } from 'types/recommendations';

import { RECOMMENDATION_CATEGORIES } from './constants';

type RecommendationsContextType = {
  company: CompanyDataType;
  categoryOptions: SelectAdvancedFilterOptionType[];
  communityOptions: SelectAdvancedFilterOptionType[];
  companyOptions: SelectAdvancedFilterOptionType[];
  careTypeOptions: SelectAdvancedFilterOptionType[];
  unitTypeOptions: SelectAdvancedFilterOptionType[];
  filters: RecommendationFilterType;
  setCompany: (company: CompanyDataType) => void;
  updateFilters: (filters: RecommendationFilterType) => void;
  resetFilters: () => void;
};

const RecommendationsContext = createContext<RecommendationsContextType | null>(null);

export const RecommendationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { selectedCommunity, communities, companies, apartmentTypes, contextCompany } = useAppSelector(
    (state) => state.global
  );

  const [company, setCompany] = useState<CompanyDataType | null>(null);

  const [filters, setFilters] = useState<RecommendationFilterType>({
    communities: [selectedCommunity?.id].filter(Boolean) as number[],
    careTypes: [],
    categories: [],
  });

  const categoryOptions: SelectAdvancedFilterOptionType[] = Object.values(RECOMMENDATION_CATEGORIES).map(
    (category) => ({
      value: category.value,
      label: category.label,
    })
  );

  const communityOptions: SelectAdvancedFilterOptionType[] = (communities ?? []).map((community: CommunityType) => ({
    value: community.id,
    label: community.comunity_name,
  }));

  const companyOptions: SelectAdvancedFilterOptionType[] = (companies ?? []).map((company: CompanyDataType) => ({
    value: company.id,
    label: company.name,
  }));

  const careTypeOptions: SelectAdvancedFilterOptionType[] = WIDGET_FILTER_OPTIONS[WIDGET_FILTER_TYPES.CARE_TYPE]
    .filter((i) => i.id)
    .map((item) => ({
      label: item.name,
      value: Number(item.id),
    }));

  const unitTypeOptions: SelectAdvancedFilterOptionType[] = apartmentTypes
    .filter((i) => i.id)
    .map((item) => ({
      label: item.type,
      value: Number(item.id),
    }));

  const updateFilters = (newFilters: RecommendationFilterType) => {
    setFilters((draft) => ({ ...draft, ...newFilters }));
  };

  const resetFilters = () => {
    setFilters({
      communities: [selectedCommunity?.id].filter(Boolean) as number[],
      careTypes: [],
      categories: [],
    });
  };

  const switchContextCompany = async (nextCompany: CompanyDataType) => {
    const response = await setContextCompany(nextCompany.id);
    if (response.accessToken) {
      dispatch(handleUpdateAccessToken(response.accessToken));
    }
    dispatch(handleSetContextCompany(response.company));
  };

  useEffect(() => {
    if (contextCompany) {
      setCompany(contextCompany);
    } else if ((companies ?? []).length > 0) {
      const firstCompany = companies[0];
      setCompany(firstCompany);
    }
  }, [contextCompany, companies]);

  if (!company) {
    return null;
  }

  const value = {
    company,
    categoryOptions,
    communityOptions,
    companyOptions,
    careTypeOptions,
    unitTypeOptions,
    filters,
    setCompany: switchContextCompany,
    updateFilters,
    resetFilters,
  };
  return <RecommendationsContext.Provider value={value}>{children}</RecommendationsContext.Provider>;
};

const useRecommendationsContext = () => {
  const context = React.useContext(RecommendationsContext);
  if (!context) {
    throw new Error('useRecommendationsContext must be used within a RecommendationsProvider');
  }
  return context;
};

export default useRecommendationsContext;
